import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Empty, Spinner, fmtNum, fmtKg } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { QrCode, Trash2, Send, X, CheckCircle2, AlertCircle } from "lucide-react";

const CATS = [
  { k: "cx1",  label: "CX1",  color: "bg-red-100 text-red-800 border-red-300" },
  { k: "cx2",  label: "CX2",  color: "bg-orange-100 text-orange-800 border-orange-300" },
  { k: "cx3",  label: "CX3",  color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { k: "cx3g", label: "CX3G", color: "bg-sky-100 text-sky-800 border-sky-300" },
  { k: "cx4",  label: "CX4",  color: "bg-purple-100 text-purple-800 border-purple-300" },
];

const corDot = {
  Amarela: "bg-yellow-400",
  Branca: "bg-white border border-ink-300",
  Azul: "bg-blue-500",
  Preta: "bg-black",
  Dourada: "bg-amber-600",
};

export default function Expedicao() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const shipmentId = searchParams.get("shipment") || "";
  const editingShipment = !!shipmentId;
  const [q, setQ] = useState("");
  const [scans, setScans] = useState([]); // pallet objects with .quantidade_bipada
  const [pending, setPending] = useState(null); // pallet aguardando confirmação
  const [pendingQty, setPendingQty] = useState("");
  const [success, setSuccess] = useState(null); // {totalPallets, totalCaixas, prefillReady}
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState(null); // saída em edição (quando editingShipment=true)
  const [loadingShip, setLoadingShip] = useState(editingShipment);
  const inputRef = useRef(null);

  // Foco no input ao montar
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Restaura sessão pendente do localStorage (só quando NÃO está editando shipment)
  useEffect(() => {
    if (editingShipment) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem("expedicao_scans") || "[]");
      if (Array.isArray(saved) && saved.length > 0) setScans(saved);
    } catch (_) { /* ignore */ }
  }, [editingShipment]);

  // Carrega saída + pallets iniciais quando em modo edição
  useEffect(() => {
    if (!editingShipment) return;
    (async () => {
      try {
        const { data: sh } = await api.get(`/shipments/${shipmentId}`);
        setShip(sh);
        const ids = sh.pallet_ids || [];
        // Mapa exato da bipagem por pallet (salvo desde nov/2026)
        const bipMap = {};
        (sh.pallets_bipagem || []).forEach((b) => { if (b && b.pallet_id) bipMap[b.pallet_id] = Number(b.quantidade || 0); });
        // Fallback legado: distribui itens[cat].quantidade proporcional entre pallets da mesma (invoice, cat)
        const hasBipMap = Object.keys(bipMap).length > 0;
        const loaded = [];
        for (const pid of ids) {
          try {
            const { data: pl } = await api.get(`/pallets/${pid}`);
            loaded.push(pl);
          } catch (_) { /* pallet apagado? ignora */ }
        }
        let qtyByPallet = {};
        if (hasBipMap) {
          qtyByPallet = bipMap;
        } else {
          // Fallback: reconstitui distribuindo proporcionalmente do itens[cat] da saída
          const totalsPerCat = {}; // key = invoice|cat, val = qty
          (sh.itens || []).forEach((it) => {
            const inv = it.invoice_id;
            ["cx1", "cx2", "cx3", "cx3g", "cx4"].forEach((cat) => {
              const q = Number(((it[cat] || {}).quantidade) || 0);
              if (q > 0) totalsPerCat[`${inv}|${cat}`] = q;
            });
          });
          const gruposPl = {};
          loaded.forEach((pl) => {
            const it0 = (pl.itens || [])[0] || {};
            const key = `${it0.invoice_id}|${it0.categoria}`;
            gruposPl[key] = gruposPl[key] || [];
            gruposPl[key].push(pl);
          });
          Object.entries(gruposPl).forEach(([key, plist]) => {
            const saidaQty = totalsPerCat[key] || 0;
            if (saidaQty <= 0) { plist.forEach((pl) => { qtyByPallet[pl.id] = 0; }); return; }
            // Teto por pallet = min(total_caixas, quantidade_expedida)
            const caps = plist.map((pl) => {
              const total = Math.max(0, Number(pl.total_caixas || 0));
              const exp = Math.max(0, Number(pl.quantidade_expedida || 0));
              return exp > 0 ? Math.min(total, exp) : total;
            });
            const totalCap = caps.reduce((a, b) => a + b, 0) || 1;
            let distribuidos = 0;
            plist.forEach((pl, i) => {
              const cap = caps[i];
              let qty = i === plist.length - 1 ? Math.max(0, saidaQty - distribuidos) : Math.round(saidaQty * (cap / totalCap));
              qty = Math.min(qty, cap);
              qtyByPallet[pl.id] = qty;
              distribuidos += qty;
            });
          });
        }
        const scansFromShip = loaded
          .filter((pl) => (qtyByPallet[pl.id] || 0) > 0)
          .map((pl) => {
            const qtd = Number(qtyByPallet[pl.id] || 0);
            return { ...pl, quantidade_bipada: qtd, total_caixas: qtd, _fromShipment: true, _originalId: pl.id };
          });
        setScans(scansFromShip);
      } catch (e) {
        showMsg("err", formatErr(e));
      } finally {
        setLoadingShip(false);
      }
    })();
    // eslint-disable-next-line
  }, [shipmentId, editingShipment]);

  useEffect(() => {
    if (editingShipment) return; // não persiste quando editando
    sessionStorage.setItem("expedicao_scans", JSON.stringify(scans));
  }, [scans, editingShipment]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const scan = async (e) => {
    e.preventDefault();
    const val = q.trim();
    if (!val) return;
    setBusy(true);
    try {
      const { data: pl } = await api.get(`/pallets/resolve`, { params: { q: val } });
      if (scans.some((s) => s.id === pl.id)) {
        const dup = scans.find((s) => s.id === pl.id);
        if (dup?._fromShipment) {
          showMsg("warn", `${pl.numero} já está bipado nesta saída.`);
        } else {
          showMsg("warn", `${pl.numero} já foi bipado nesta sessão.`);
        }
      } else {
        const rest = Number(pl.restante ?? pl.total_caixas ?? 0);
        if (rest <= 0) {
          showMsg("warn", `${pl.numero} já foi totalmente expedido.`);
        } else {
          setPending(pl);
          setPendingQty(String(Math.floor(rest)));
        }
      }
    } catch (e2) {
      showMsg("err", formatErr(e2));
    } finally {
      setQ("");
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const confirmScan = () => {
    if (!pending) return;
    const rest = Number(pending.restante ?? pending.total_caixas ?? 0);
    const qty = Number(pendingQty);
    const isGranel = !!pending.granel;
    const unit = isGranel ? "kg" : "caixas";
    if (!qty || qty <= 0) { showMsg("err", "Quantidade inválida"); return; }
    if (qty > rest + 0.001) { showMsg("err", `Máximo permitido: ${isGranel ? rest.toFixed(2) : Math.floor(rest)} ${unit}`); return; }
    const p = { ...pending, quantidade_bipada: qty };
    // ajusta a quantidade do item[0] (categoria) para bater com o bipado
    if (p.itens && p.itens[0]) p.itens[0] = { ...p.itens[0], quantidade: qty };
    p.total_caixas = qty;
    setScans((prev) => [p, ...prev]);
    const parcial = qty < rest - 0.001;
    showMsg("ok", parcial
      ? `${pending.numero}: ${isGranel ? qty.toFixed(2) : qty} ${unit} bipados · ${isGranel ? (rest - qty).toFixed(2) : Math.floor(rest - qty)} ${unit} continuam no estoque`
      : `${pending.numero} bipado por completo (${isGranel ? qty.toFixed(2) : qty} ${unit})`);
    setPending(null); setPendingQty("");
    inputRef.current?.focus();
  };

  const cancelScan = () => { setPending(null); setPendingQty(""); inputRef.current?.focus(); };

  const remove = (id) => setScans((prev) => prev.filter((s) => s.id !== id));
  const clearAll = () => {
    if (scans.length === 0 || window.confirm("Descartar todos os pallets bipados?")) setScans([]);
  };

  // ---- Agregações ----
  const TIPO_LABELS = { amarela: "Amarela", roxa: "Roxa", branca: "Branca", pelada: "Pelada" };
  const TIPO_DOTS = { amarela: "bg-yellow-400", roxa: "bg-purple-600", branca: "bg-white border border-ink-300", pelada: "bg-stone-400" };
  const byCat = CATS.map((c) => {
    const items = scans.filter((s) => (s.itens || [])[0]?.categoria === c.k);
    const totalCaixas = items.reduce((a, p) => a + Number(p.quantidade_bipada || p.total_caixas || 0), 0);
    // Sub-agregação por tipo_cebola dentro desta categoria
    const porTipo = {};
    items.forEach((p) => {
      const it = (p.itens || [])[0] || {};
      const t = (it.tipo_cebola || "amarela").toLowerCase();
      porTipo[t] = porTipo[t] || { count: 0, caixas: 0 };
      porTipo[t].count += 1;
      porTipo[t].caixas += Number(p.quantidade_bipada || p.total_caixas || 0);
    });
    return { ...c, count: items.length, totalCaixas, porTipo };
  });
  const totalPallets = scans.length;
  // Separa caixas (cx) de granel (kg). Granel = pallet.granel === true OU categoria === "granel"
  const scansCaixas = scans.filter((s) => !(s.granel || (s.itens || [])[0]?.categoria === "granel"));
  const scansGranel = scans.filter((s) => s.granel || (s.itens || [])[0]?.categoria === "granel");
  const totalCaixasGeral = scansCaixas.reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0);
  const totalKgGranel = scansGranel.reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0);
  const pesoTotal = (totalCaixasGeral * 20) + totalKgGranel;
  const hasGranel = scansGranel.length > 0;
  const totalPalletsGranel = scansGranel.length;

  // Envia para o backend: consome pallets e cria uma Saída em status "pendente"
  const sendToShipment = async () => {
    if (scans.length === 0 && !editingShipment) return;
    setBusy(true);
    try {
      if (editingShipment) {
        // PATCH: reconstrói a saída com os pallets atuais.
        // Backend faz diff automático (reverte removidos, expedita novos).
        const payload = {
          pallets: scans.map((p) => ({
            pallet_id: p.id,
            quantidade: Number(p.quantidade_bipada || p.total_caixas || 0),
          })),
        };
        const { data: shipment } = await api.patch(`/shipments/${shipmentId}/expedicao-pallets`, payload);
        const summary = {
          totalPallets: scans.length,
          totalCaixas: scans.filter((s) => !(s.granel || (s.itens || [])[0]?.categoria === "granel")).reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0),
          totalKg: scans.filter((s) => s.granel || (s.itens || [])[0]?.categoria === "granel").reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0),
          nfs: new Set(scans.map((s) => (s.itens || [])[0]?.invoice_id).filter(Boolean)).size,
          romaneio: shipment.romaneio_saida_numero,
          shipmentId: shipment.id,
          updated: true,
        };
        setSuccess(summary);
        return;
      }

      // Fluxo original: criar NOVA saída em rascunho
      // 1) Consome cada pallet no backend (só a quantidade bipada — o resto volta pro estoque naturalmente)
      for (const p of scans) {
        const qty = Number(p.quantidade_bipada || p.total_caixas || 0);
        if (qty > 0) {
          await api.post(`/pallets/${p.id}/expedir`, { quantidade: qty });
        }
      }
      // 2) Monta itens agregados por invoice_id no formato do Shipment
      const grouped = {};
      scans.forEach((p) => {
        const it = (p.itens || [])[0] || {};
        const key = it.invoice_id;
        if (!key) return;
        grouped[key] = grouped[key] || {
          invoice_id: it.invoice_id,
          cx1: { quantidade: 0 }, cx2: { quantidade: 0 }, cx3: { quantidade: 0 },
          cx3g: { quantidade: 0 }, cx4: { quantidade: 0 },
        };
        const cat = it.categoria;
        const qBip = Number(p.quantidade_bipada || p.total_caixas || 0);
        // Pallet granel: acumula em bloco `granel` (kg)
        if (cat === "granel" || p.granel) {
          if (!grouped[key].granel) grouped[key].granel = { quantidade: 0, unidade: "kg" };
          grouped[key].granel.quantidade += qBip;
        } else if (cat && grouped[key][cat]) {
          grouped[key][cat].quantidade += qBip;
        }
      });
      // 3) Cria a Saída em status "pendente"
      const { data: shipment } = await api.post("/shipments/from-expedicao", {
        itens: Object.values(grouped),
        pallet_ids: scans.map((s) => s.id),
        pallets_bipagem: scans.map((s) => ({
          pallet_id: s.id,
          quantidade: Number(s.quantidade_bipada || s.total_caixas || 0),
        })),
      });
      const summary = {
        totalPallets: scans.length,
        totalCaixas: scans.filter((s) => !(s.granel || (s.itens || [])[0]?.categoria === "granel")).reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0),
        totalKg: scans.filter((s) => s.granel || (s.itens || [])[0]?.categoria === "granel").reduce((a, s) => a + Number(s.quantidade_bipada || s.total_caixas || 0), 0),
        nfs: Object.values(grouped).length,
        romaneio: shipment.romaneio_saida_numero,
        shipmentId: shipment.id,
      };
      sessionStorage.removeItem("expedicao_scans");
      setScans([]);
      setSuccess(summary);
    } catch (e) {
      showMsg("err", formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loadingShip) {
    return (
      <Layout title="Expedição — Carregando saída..." subtitle="Buscando pallets já bipados">
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      </Layout>
    );
  }

  const romaneioLabel = ship ? `RS-${String(ship.romaneio_saida_numero || 0).padStart(4, "0")}` : "";

  return (
    <Layout
      title={editingShipment ? `Expedição — Editando ${romaneioLabel}` : "Expedição — Bipagem de Pallets"}
      subtitle={editingShipment ? `${ship?.cliente || "Sem cliente"} · adicione ou remova pallets bipados` : "Escaneie os QR Codes dos pallets prontos para expedição"}
      actions={
        <div className="flex gap-2">
          {editingShipment && (
            <Button variant="secondary" onClick={() => nav(`/shipments/${shipmentId}`)} data-testid="back-to-shipment-btn">
              <X size={16} /> Voltar à Saída
            </Button>
          )}
          {!editingShipment && scans.length > 0 && <Button variant="secondary" onClick={clearAll}><Trash2 size={16} /> Limpar</Button>}
          <Button onClick={sendToShipment} disabled={busy || (scans.length === 0 && !editingShipment)} data-testid="send-to-shipment-btn">
            {busy ? <Spinner size={16} /> : <><Send size={16} /> {editingShipment ? `Atualizar ${romaneioLabel} (${scans.length})` : `Enviar para Saída (${scans.length})`}</>}
          </Button>
        </div>
      }
    >
      {/* Área de scan */}
      <Card className="p-5 mb-5 bg-emerald-50/40 border-emerald-200">
        <form onSubmit={scan} className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-500 text-white flex items-center justify-center flex-shrink-0">
            <QrCode size={24} />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold uppercase tracking-[0.1em] text-emerald-700 mb-1">Bipe / cole o QR <span className="normal-case font-normal text-emerald-600 opacity-80">— ou digite só o número da etiqueta (ex.: 3312)</span></label>
            <input
              ref={inputRef}
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Bipe o QR ou digite o nº da etiqueta (ex: 3312 · P-3312 · uuid)"
              className="w-full rounded-md bg-white border border-emerald-300 px-3 py-3 text-base font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
              autoComplete="off"
              data-testid="qr-scan-input"
            />
          </div>
          <Button type="submit" disabled={busy || !q.trim()} data-testid="qr-scan-btn">
            {busy ? <Spinner size={16} /> : <>Bipar</>}
          </Button>
        </form>
        {msg && (
          <div className={`mt-3 px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
            msg.type === "ok" ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
            : msg.type === "warn" ? "bg-orange-100 text-orange-800 border border-orange-300"
            : "bg-red-100 text-danger border border-red-300"
          }`} data-testid="scan-msg">
            {msg.type === "ok" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {msg.text}
          </div>
        )}
      </Card>

      {/* Resumo */}
      <div className={`grid grid-cols-2 ${hasGranel ? "md:grid-cols-5" : "md:grid-cols-4"} gap-3 mb-5`}>
        <Card className="p-3">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Pallets bipados</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1">{totalPallets}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Total caixas</div>
          <div className="font-mono text-2xl font-semibold text-brand-500 mt-1" data-testid="total-caixas">{fmtNum(totalCaixasGeral, 0)}</div>
        </Card>
        {hasGranel && (
          <Card className="p-3 bg-amber-50 border-amber-200">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-amber-700">Total granel</div>
            <div className="font-mono text-2xl font-semibold text-amber-800 mt-1" data-testid="total-granel-kg">
              {fmtNum(totalKgGranel, 2)} <span className="text-sm">kg</span>
            </div>
            <div className="text-[10px] text-amber-700 opacity-75 mt-0.5">{totalPalletsGranel} pallet{totalPalletsGranel !== 1 ? "s" : ""}</div>
          </Card>
        )}
        <Card className="p-3">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Peso estimado</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1" data-testid="peso-estimado">{fmtKg(pesoTotal)}</div>
          {hasGranel && (
            <div className="text-[10px] text-ink-500 opacity-75 mt-0.5">
              cx: {fmtKg(totalCaixasGeral * 20)} + granel: {fmtKg(totalKgGranel)}
            </div>
          )}
        </Card>
        <Card className="p-3">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">NFs envolvidas</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1">
            {new Set(scans.map((s) => (s.itens || [])[0]?.invoice_id).filter(Boolean)).size}
          </div>
        </Card>
      </div>

      {/* Por categoria — decomposto por tipo de cebola quando há mais de 1 */}
      <div className={`grid grid-cols-2 ${hasGranel ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3 mb-5`}>
        {byCat.map((c) => {
          const tipos = Object.entries(c.porTipo);
          return (
            <div key={c.k} className={`border rounded-lg p-3 ${c.color}`}>
              <div className="font-bold">{c.label}</div>
              <div className="text-2xl font-mono font-bold mt-1">{fmtNum(c.totalCaixas, 0)}</div>
              <div className="text-xs opacity-70">{c.count} pallet{c.count !== 1 ? "s" : ""}</div>
              {tipos.length > 0 && (
                <div className="mt-2 pt-2 border-t border-current border-opacity-20 space-y-1">
                  {tipos.map(([tipo, agg]) => (
                    <div key={tipo} className="flex items-center gap-1.5 text-[11px]" data-testid={`cat-${c.k}-tipo-${tipo}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${TIPO_DOTS[tipo] || "bg-stone-300"}`}></span>
                      <span className="font-medium">{TIPO_LABELS[tipo] || tipo}</span>
                      <span className="ml-auto font-mono font-bold">{fmtNum(agg.caixas, 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {hasGranel && (
          <div className="border rounded-lg p-3 bg-amber-100 text-amber-900 border-amber-400" data-testid="cat-granel-card">
            <div className="font-bold flex items-center gap-1">
              GRANEL
              <span className="text-[9px] font-normal opacity-70">(bruta)</span>
            </div>
            <div className="text-2xl font-mono font-bold mt-1">
              {fmtNum(totalKgGranel, 2)}
              <span className="text-sm font-normal ml-1">kg</span>
            </div>
            <div className="text-xs opacity-70">{totalPalletsGranel} pallet{totalPalletsGranel !== 1 ? "s" : ""}</div>
          </div>
        )}
      </div>

      {/* Lista de pallets */}
      <Card className="p-5">
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">
          Pallets bipados ({scans.length})
        </div>
        {scans.length === 0 ? (
          <Empty title="Aguardando bipagem" hint="Escaneie um QR Code acima para começar." />
        ) : (
          <div className="divide-y divide-ink-200">
            {scans.map((p) => {
              const it = (p.itens || [])[0] || {};
              const isGranel = !!p.granel || it.categoria === "granel";
              const cat = CATS.find((c) => c.k === it.categoria);
              const catBadge = isGranel
                ? { label: "GRL", color: "bg-amber-200 text-amber-900 border-amber-400" }
                : cat || { label: "?", color: "bg-stone-100" };
              const qtd = Number(p.quantidade_bipada ?? it.quantidade ?? 0);
              const unit = isGranel ? "kg" : "cx";
              return (
                <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`scanned-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${catBadge.color}`}>
                      {catBadge.label}
                    </div>
                    <div>
                      <div className="font-mono font-semibold text-ink-900 flex items-center gap-2">
                        {p.numero}
                        {isGranel && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" data-testid={`granel-badge-${p.id}`}>
                            granel
                          </span>
                        )}
                        {p._fromShipment && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`from-shipment-badge-${p.id}`}>
                            Já bipado
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-500 flex items-center gap-2 flex-wrap">
                        <span>NF #{it.nota_numero || p.nota_numero || "?"}</span>
                        <span>·</span>
                        <span>{it.fornecedor || p.fornecedor || "—"}</span>
                        <span>·</span>
                        <span className="font-mono">{fmtNum(qtd, isGranel ? 2 : 0)} {unit}</span>
                        {it.tipo_cebola && (
                          <span className="flex items-center gap-1 font-semibold text-ink-700">
                            <span>·</span>
                            <span className={`w-2.5 h-2.5 rounded-full ${TIPO_DOTS[(it.tipo_cebola || '').toLowerCase()] || "bg-stone-300"}`}></span>
                            <span>{TIPO_LABELS[(it.tipo_cebola || '').toLowerCase()] || it.tipo_cebola}</span>
                          </span>
                        )}
                        {it.sacaria_cor && (
                          <span className="flex items-center gap-1">
                            <span>·</span>
                            <span className={`w-2.5 h-2.5 rounded-full ${corDot[it.sacaria_cor] || "bg-stone-300"}`}></span>
                            <span>{it.sacaria_tipo} {it.sacaria_cor}</span>
                          </span>
                        )}
                        {it.ignorar_sacaria && <><span>·</span><span className="italic">sem sacaria</span></>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} className="p-2 rounded-md hover:bg-red-100 text-danger" title="Remover">
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Popup de sucesso — carga finalizada */}
      {success && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6" data-testid="success-modal">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 size={40} className="text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-ink-900">{success.updated ? "Saída atualizada!" : "Carga finalizada!"}</h3>
              <p className="text-sm text-ink-500 mt-1">
                Saída <strong className="font-mono">RS-{String(success.romaneio || 0).padStart(4, "0")}</strong> {success.updated ? "atualizada com sucesso." : <>criada em <strong>Pendente</strong>.</>}
              </p>

              <div className={`grid ${(success.totalKg || 0) > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-3 w-full mt-5`}>
                <div className="p-3 rounded-lg bg-stone-50 border border-ink-200">
                  <div className="text-xs text-ink-500">Pallets</div>
                  <div className="font-mono text-2xl font-bold text-ink-900">{success.totalPallets}</div>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 border border-ink-200">
                  <div className="text-xs text-ink-500">Caixas</div>
                  <div className="font-mono text-2xl font-bold text-brand-500">{fmtNum(success.totalCaixas, 0)}</div>
                </div>
                {(success.totalKg || 0) > 0 && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="text-xs text-amber-700">Granel</div>
                    <div className="font-mono text-2xl font-bold text-amber-800">
                      {fmtNum(success.totalKg, 2)} <span className="text-sm">kg</span>
                    </div>
                  </div>
                )}
                <div className="p-3 rounded-lg bg-stone-50 border border-ink-200">
                  <div className="text-xs text-ink-500">NFs</div>
                  <div className="font-mono text-2xl font-bold text-ink-900">{success.nfs}</div>
                </div>
              </div>

              <div className="text-xs text-ink-500 mt-4 text-center">
                {success.updated ? "As alterações foram salvas. Você pode voltar para a saída para conferir." : "Preencha cliente e preços em Saídas para finalizar."}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-5 w-full">
                {success.updated ? (
                  <>
                    <Button variant="secondary" className="flex-1" onClick={() => setSuccess(null)} data-testid="continue-bipping-btn">
                      Continuar editando
                    </Button>
                    <Button className="flex-1" onClick={() => { setSuccess(null); nav(`/shipments/${success.shipmentId}`); }} data-testid="go-shipments-btn">
                      Ir para Saída
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" className="flex-1" onClick={() => setSuccess(null)} data-testid="continue-bipping-btn">
                      Continuar bipando
                    </Button>
                    <Button className="flex-1" onClick={() => { setSuccess(null); nav("/shipments"); }} data-testid="go-shipments-btn">
                      Ir para Saídas
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de confirmação de quantidade */}
      {pending && (() => {
        const rest = Number(pending.restante ?? pending.total_caixas ?? 0);
        const it = (pending.itens || [])[0] || {};
        const cat = CATS.find((c) => c.k === it.categoria);
        const isGranel = !!pending.granel || it.categoria === "granel";
        const unit = isGranel ? "kg" : "caixas";
        const qty = Number(pendingQty) || 0;
        const parcial = qty > 0 && qty < rest - 0.001;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-6" data-testid="confirm-scan-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Confirmar Bipagem</div>
                  <div className="font-mono font-bold text-xl text-ink-900 mt-1">
                    {pending.numero}
                    {isGranel && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold uppercase">granel</span>}
                  </div>
                  <div className="text-sm text-ink-700">NF #{it.nota_numero} · {it.fornecedor}</div>
                </div>
                <button onClick={cancelScan} className="p-1 hover:bg-stone-100 rounded"><X size={20} /></button>
              </div>

              <div className={`p-3 rounded-md mb-4 ${isGranel ? "bg-amber-50" : (cat?.color || "bg-stone-100")}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold">{isGranel ? "Cebola a granel (kg)" : cat?.label}</span>
                  <span className="font-mono text-xs">
                    Total pallet: <strong>{fmtNum(pending.total_caixas, isGranel ? 2 : 0)} {unit}</strong>
                    {pending.quantidade_expedida > 0 && (
                      <> · Já expedido: <strong>{fmtNum(pending.quantidade_expedida, isGranel ? 2 : 0)}</strong></>
                    )}
                    · Disponível: <strong>{fmtNum(rest, isGranel ? 2 : 0)}</strong>
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Quantidade a bipar ({unit})</label>
                <input
                  type="number" min="0" step={isGranel ? "0.01" : "1"} max={isGranel ? rest : Math.floor(rest)} autoFocus
                  value={pendingQty}
                  onChange={(e) => setPendingQty(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmScan(); } }}
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-3 text-2xl font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="confirm-qty-input"
                />
                {parcial && (
                  <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                    Bipagem parcial: <strong>{fmtNum(rest - qty, isGranel ? 2 : 0)} {unit}</strong> permanecerão no estoque do pallet {pending.numero} para outra saída.
                  </div>
                )}
                {Math.abs(qty - rest) < 0.001 && qty > 0 && (
                  <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                    Pallet será totalmente expedido.
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={cancelScan} data-testid="cancel-scan-btn">Cancelar</Button>
                <Button onClick={confirmScan} data-testid="confirm-scan-btn">
                  <CheckCircle2 size={16} /> Confirmar
                </Button>
              </div>
            </Card>
          </div>
        );
      })()}
    </Layout>
  );
}
